import { get, post, put } from './fetcher';

/**
 * User API
 */

const USER_ENDPOINTS = {
  REGISTER: '/api/user/register',
  LINK_LOCAL: '/api/user/link-local',
  LOGIN: '/api/user/login',
  NAVER_LOGIN: '/api/user/sso/naver',
  REFRESH: '/api/user/refresh',
  LOGOUT: '/api/user/logout',
  ME: '/api/user/me',
  INVESTMENT_META: '/api/user/investment-meta',
  CHANGE_PASSWORD: '/api/user/change-password',
  CHECK_DUPLICATE: '/api/user/check-duplicate',
};

/**
 * 중복 체크 (아이디/이메일)
 * @param {string} userId - 체크할 아이디 (선택)
 * @param {string} email - 체크할 이메일 (선택)
 * @returns {Promise<object>} 중복 체크 결과
 */
export const checkDuplicate = async (userId = null, email = null) => {
  const params = {};
  if (userId) params.userId = userId;
  if (email) params.email = email;

  return get(USER_ENDPOINTS.CHECK_DUPLICATE, params);
};

/**
 * 회원가입
 * @param {object} data - 회원가입 정보
 * @returns {Promise<object>} 회원가입 결과
 */
export const register = async (data) => {
  const requestData = {
    userId: data.userId,
    email: data.email,
    password: data.password,
    userName: data.userName,
    nickname: data.nickname,
    phone: data.phone || null,
    birthDate: data.birthDate || null,
    gender: data.gender || null,
  };

  return post(USER_ENDPOINTS.REGISTER, requestData);
};

/**
 * 소셜 계정에 이메일(아이디/비밀번호) 로그인 연동.
 * SNS 로그인으로 소유 검증이 끝난 뒤(토큰 보유) 호출한다.
 * @param {string} token - 방금 SNS 로그인으로 발급된 JWT
 * @param {string} userId - 연동할 아이디
 * @param {string} password - 연동할 비밀번호
 */
export const linkLocal = async (token, userId, password) => {
  return post(USER_ENDPOINTS.LINK_LOCAL, { userId, password }, token, { skipAuthRefresh: true });
};

/**
 * 로그인
 * @param {string} userId - 사용자 ID
 * @param {string} password - 비밀번호
 * @returns {Promise<object>} 로그인 결과 (token, user)
 */
export const login = async (userId, password) => {
  const requestData = {
    userId,
    password,
  };

  return post(USER_ENDPOINTS.LOGIN, requestData);
};

/**
 * 네이버 소셜 로그인 (콜백에서 받은 code/state 를 백엔드로 전달)
 * @param {string} code - 네이버 인가 코드
 * @param {string} state - CSRF 방지용 state
 * @returns {Promise<object>} 로그인 결과 (token, refreshToken, user)
 */
export const naverLogin = async (code, state) => {
  return post(USER_ENDPOINTS.NAVER_LOGIN, { code, state }, null, { skipAuthRefresh: true });
};

/**
 * 토큰 재발급
 * @param {string} refreshTokenValue - refresh token
 * @returns {Promise<object>} 재발급 결과
 */
export const refreshToken = async (refreshTokenValue) => {
  return post(USER_ENDPOINTS.REFRESH, {
    refreshToken: refreshTokenValue,
  }, null, { skipAuthRefresh: true });
};

/**
 * 로그아웃
 * @param {string} refreshTokenValue - refresh token
 * @returns {Promise<object>} 로그아웃 결과
 */
export const logout = async (refreshTokenValue = null) => {
  return post(USER_ENDPOINTS.LOGOUT, {
    refreshToken: refreshTokenValue,
  }, null, { skipAuthRefresh: true });
};

/**
 * 내 정보 조회
 * @param {string} token - JWT 토큰
 * @returns {Promise<object>} 사용자 정보
 */
export const getMyInfo = async (token) => {
  return get(USER_ENDPOINTS.ME, {}, token);
};

/**
 * 내 정보 수정
 * @param {string} token - JWT 토큰
 * @param {object} data - 수정할 정보
 * @returns {Promise<object>} 수정 결과
 */
export const updateMyInfo = async (token, data) => {
  return put(USER_ENDPOINTS.ME, data, token);
};

export const getMyInvestmentMeta = async (token) => {
  return get(USER_ENDPOINTS.INVESTMENT_META, {}, token);
};

export const upsertMyInvestmentMeta = async (token, data) => {
  return put(USER_ENDPOINTS.INVESTMENT_META, data, token);
};

/**
 * 비밀번호 변경
 * @param {string} token - JWT 토큰
 * @param {string} currentPassword - 현재 비밀번호
 * @param {string} newPassword - 새 비밀번호
 * @returns {Promise<object>} 변경 결과
 */
export const changePassword = async (token, currentPassword, newPassword) => {
  return post(USER_ENDPOINTS.CHANGE_PASSWORD, {
    currentPassword,
    newPassword,
  }, token);
};
